import type { ActorDatabase } from "@-/dev.improve.haniverse.database.actor";
import type { ContentDatabase } from "@-/dev.improve.haniverse.database.content";
import { uniq } from "@-/com.npmjs.array-utils-ts";
import runes from "@-/com.npmjs.runes2";
import {
  enrollGraphemeCardTargets,
  type GraphemeCardTarget,
  isGraphemePronunciationEnrollable,
} from "#create-user-card";

const WHITESPACE_REGEX = /\s+/u;

/** A tone-number syllable in a form comparable across sources (`Lu:4`, `lü4` → `lv4`). */
function toSyllableKey(syllable: string): string {
  return syllable.toLowerCase().replaceAll("u:", "v").replaceAll("ü", "v");
}

export type CreateUserWordReadingCardsArgs = {
  actorDb: ActorDatabase;
  contentDb: ContentDatabase;
  /** Tone-number pinyin for the whole word, one syllable per character (`dai4 fu0`). */
  pinyin: string;
  startedStudyingAt?: string;
  word: string;
};

/**
 * Introduce the non-main character readings a word uses. A character brings only its main reading
 * and the readings at or below its own curriculum grade into the deck (see `createUserCard`); every
 * higher reading is introduced here, by the first word that uses it: adding 大夫 (`dai4 fu0`)
 * enrolls 大's `dai4` pronunciation card.
 *
 * Idempotent: a reading already in the deck keeps its schedule. A word whose pinyin does not
 * align one syllable per character (erhua, missing reading) introduces nothing.
 */
export async function createUserWordReadingCards({
  actorDb,
  contentDb,
  pinyin,
  startedStudyingAt = new Date().toISOString(),
  word,
}: CreateUserWordReadingCardsArgs): Promise<void> {
  const graphemes = runes(word);
  const syllableKeys = pinyin
    .trim()
    .split(WHITESPACE_REGEX)
    .map((syllable) => toSyllableKey(syllable));
  if (graphemes.length !== syllableKeys.length) {
    return;
  }
  const usedReadingKeys = new Set(
    graphemes.map((grapheme, index) => `${grapheme}\u0000${syllableKeys[index]}`),
  );

  const [secondaryReadingRows, meaningRows] = await Promise.all([
    contentDb(import.meta.location).query.appGraphemePronunciationCard.findMany({
      columns: { grapheme: true, pinyin: true },
      where: { grapheme: { in: uniq(graphemes) }, isMain: { eq: false } },
    }),
    contentDb(import.meta.location).query.appGraphemeMeaningCard.findMany({
      columns: { grapheme: true, label: true },
      where: { grapheme: { in: uniq(graphemes) } },
    }),
  ]);
  const enrollableGraphemes = new Set(
    meaningRows
      .filter(({ label }) => isGraphemePronunciationEnrollable(label))
      .map(({ grapheme }) => grapheme),
  );
  const targetList = secondaryReadingRows
    .filter(
      ({ grapheme, pinyin: readingPinyin }) =>
        enrollableGraphemes.has(grapheme) &&
        usedReadingKeys.has(`${grapheme}\u0000${toSyllableKey(readingPinyin)}`),
    )
    .map((row): GraphemeCardTarget => ({
      grapheme: row.grapheme,
      kind: "pronunciation",
      pinyin: row.pinyin,
    }));
  if (targetList.length === 0) {
    return;
  }

  await actorDb(import.meta.location).transaction(async (tx) =>
    enrollGraphemeCardTargets({
      dueIso: startedStudyingAt,
      nowIso: startedStudyingAt,
      scheduledDays: 0,
      startedStudyingAt,
      targetList,
      tx,
    }),
  );
}
